import { z } from 'zod';

/**
 * API Response Validation Schemas
 *
 * These schemas validate API responses at runtime to catch type mismatches
 * and prevent "cannot read property of undefined" errors in production.
 *
 * Based on 1st principles: Validate data at system boundaries (external APIs)
 */

// Product Information Schema (for getProductInfos)
// Matches actual Stripe API response structure used in Payment.vue
// API returns nested structure: {product: {...}, price: {...}}
export const ProductInfoSchema = z.object({
  product: z.object({
    name: z.string(),
    description: z.string().optional(),
    unit_label: z.string().nullable().optional()
  }),
  price: z.object({
    id: z.string(),
    unit_amount: z.number()
  })
});

export const ProductInfoArraySchema = z.array(ProductInfoSchema);

export type ProductInfo = z.infer<typeof ProductInfoSchema>;

// Pathways Schema (for getPathways)
// Matches GraphQL API response from programs.ts:43-46
// GraphQL returns: {pathway: string, count: number}
export const PathwaySchema = z.object({
  pathway: z.string(), // GraphQL field name is "pathway", not "name"
  count: z.number() // Number of programs in this pathway
});

export const PathwaysArraySchema = z.array(PathwaySchema);

export type Pathway = z.infer<typeof PathwaySchema>;

// Team Member Schema (for individual members within teams)
// Matches TeamMember interface from src/types/team.ts
// NOTE: Some members have null firstName/lastName in MongoDB (incomplete profiles)
export const TeamMemberSchema = z.object({
  _id: z.string(),
  firstName: z.string().nullish(), // Allow null or undefined - some users have incomplete profiles
  lastName: z.string().nullish(), // Allow null or undefined - some users have incomplete profiles
  email: z.string().email().nullish(),
  isMembershipPending: z.boolean().optional(),
  role: z.string().optional(),
  status: z.string().optional()
});

export type TeamMember = z.infer<typeof TeamMemberSchema>;

// Team List Item Schema (for fetchTeamList)
// Matches ProgramTeam MongoDB collection structure and TeamListItem interface from src/types/team.ts
// API returns: { _id, name, members[], owner, program_id, password, adks }
export const TeamListItemSchema = z
  .object({
    _id: z.union([z.string(), z.number()]),
    name: z.string(), // Team name - CRITICAL for display
    icon: z.string().optional(),
    members: z.array(TeamMemberSchema).optional(),
    ownerId: z.string().optional(),
    programId: z.string().optional(),
    owner: z.any().optional(), // MongoDB ObjectId
    program_id: z.any().optional(), // MongoDB ObjectId
    password: z.string().optional(),
    adks: z.array(z.any()).optional()
  })
  .passthrough(); // Allow additional MongoDB fields

export const TeamListSchema = z.array(TeamListItemSchema);

// Student Document Schema (for fetchStudentById)
// Matches MongoDB User document structure from src/@types/user.d.ts
// NOTE: firstName and lastName can be null or undefined - users may not have completed profiles
export const StudentDocumentSchema = z
  .object({
    _id: z.string(),
    firstName: z.string().nullish(), // Allow null or undefined - code uses fallbacks like `firstName || ''`
    lastName: z.string().nullish(), // Allow null or undefined - code uses fallbacks like `lastName || ''`
    email: z.string().email().nullish(),
    program_id: z.union([z.string(), z.number()]).optional(),
    adks: z.array(z.any()).optional()
    // Allow additional fields
  })
  .passthrough();

export type StudentDocument = z.infer<typeof StudentDocumentSchema>;

// Airtable Options Schema (for fetchOptionsByType)
export const AirtableOptionsSchema = z.array(z.string());

export type AirtableOptions = z.infer<typeof AirtableOptionsSchema>;

/**
 * Safe validation wrapper that logs errors to Sentry and returns fallback
 *
 * @param schema - Zod schema to validate against
 * @param data - Data to validate
 * @param fallback - Fallback value if validation fails
 * @param context - Context string for error reporting
 * @returns Validated data or fallback
 */
export function safeValidate<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  fallback: T,
  context: string
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    console.error(`[${context}] Validation failed:`, error);

    // Log to Sentry if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        tags: { validation_context: context },
        extra: { received_data: data }
      });
    }

    return fallback;
  }
}
